// 260825nrai: CodeMirror 6 editors for SQL/Ruby textareas and read-only code blocks.
// Replaces the Rails-4 CodeMirror 5 globals (CodeMirror.fromTextArea).

import { EditorView, basicSetup, minimalSetup } from 'codemirror';
import { EditorState } from '@codemirror/state';
import { keymap } from '@codemirror/view';
import { indentWithTab } from '@codemirror/commands';
import { indentUnit, StreamLanguage } from '@codemirror/language';
import { sql, MySQL } from '@codemirror/lang-sql';
import { ruby } from '@codemirror/legacy-modes/mode/ruby';
import { format as format_sql } from 'sql-formatter';

const editor_views = new WeakMap();
let format_listener_bound = false;
let tab_refresh_bound = false;

const DEFAULT_SQL_FORMAT_OPTIONS = {
  language: 'mysql',
  keywordCase: 'upper',
  tabWidth: 2,
  useTabs: true
};

function data_flag(element, attribute_name, default_value) {
  const raw = element.getAttribute(attribute_name);
  if (raw == null || raw === '') return default_value;
  return raw === 'true' || raw === attribute_name;
}

function language_extension(language_name) {
  const key = String(language_name || '').toLowerCase();
  if (key === 'sql' || key === 'mysql' || key === 'text/x-mysql') {
    return sql({ dialect: MySQL });
  }
  if (key === 'ruby' || key === 'text/x-ruby') {
    return StreamLanguage.define(ruby);
  }
  return null;
}

function is_sql_language(language_name) {
  const key = String(language_name || '').toLowerCase();
  return key === 'sql' || key === 'mysql' || key === 'text/x-mysql';
}

function editor_theme(min_height) {
  return EditorView.theme({
    '&': {
      height: 'auto',
      minHeight: min_height
    },
    '.cm-scroller': {
      overflow: 'auto',
      fontFamily: 'Monaco, "Courier New", Courier, monospace',
      fontSize: '13px',
      minHeight: min_height
    },
    '.cm-content': {
      minHeight: min_height
    }
  });
}

function editor_extensions(options) {
  const {
    language,
    line_numbers = false,
    line_wrapping = true,
    indent_with_tabs = true,
    tab_size = 2,
    read_only = false,
    min_height = '8em'
  } = options;

  const extensions = [
    ...(line_numbers ? basicSetup : minimalSetup),
    language_extension(language),
    editor_theme(min_height),
    EditorState.tabSize.of(tab_size),
    indentUnit.of(indent_with_tabs ? '\t' : ' '.repeat(tab_size))
  ].filter(Boolean);

  if (line_wrapping) extensions.push(EditorView.lineWrapping);
  if (indent_with_tabs) extensions.push(keymap.of([indentWithTab]));

  if (read_only) {
    extensions.push(EditorState.readOnly.of(true), EditorView.editable.of(false));
  }

  return extensions;
}

function options_from_textarea(textarea) {
  const language = textarea.getAttribute('data-code-editor') || 'text';
  const sql_field = is_sql_language(language);
  return {
    language,
    line_numbers: data_flag(textarea, 'data-code-editor-line-numbers', sql_field),
    line_wrapping: data_flag(textarea, 'data-code-editor-line-wrapping', true),
    indent_with_tabs: data_flag(textarea, 'data-code-editor-indent-with-tabs', true),
    tab_size: parseInt(textarea.getAttribute('data-code-editor-tab-size') || '2', 10) || 2,
    autofocus: data_flag(textarea, 'data-code-editor-autofocus', false),
    min_height: textarea.getAttribute('data-code-editor-min-height') || (sql_field ? '16em' : '8em')
  };
}

function sync_textarea(textarea, view) {
  textarea.value = view.state.doc.toString();
}

function editor_from_textarea(textarea, extensions) {
  const view = new EditorView({
    doc: textarea.value,
    extensions: [
      ...extensions,
      EditorView.updateListener.of((update) => {
        if (update.docChanged) sync_textarea(textarea, update.view);
      })
    ]
  });
  textarea.parentNode.insertBefore(view.dom, textarea);
  textarea.style.display = 'none';
  textarea.setAttribute('aria-hidden', 'true');
  editor_views.set(textarea, view);
  editor_views.set(view.dom, view);
  return view;
}

function mount_from_textarea(textarea) {
  if (textarea.dataset.codeEditorMounted === 'true') return;
  if (!textarea.getAttribute('data-code-editor')) return;

  const options = options_from_textarea(textarea);
  const should_focus = options.autofocus && textarea.offsetParent !== null;
  const view = editor_from_textarea(textarea, editor_extensions(options));
  textarea.dataset.codeEditorMounted = 'true';

  if (should_focus) view.focus();
}

function mount_readonly_block(block) {
  if (block.dataset.codeEditorMounted === 'true') return;
  const language = block.getAttribute('data-language') || block.getAttribute('data-code-editor') || 'text';
  const view = new EditorView({
    doc: block.textContent.trim(),
    extensions: editor_extensions({
      language,
      line_numbers: false,
      line_wrapping: true,
      indent_with_tabs: false,
      read_only: true,
      min_height: '2em'
    })
  });
  block.parentNode.replaceChild(view.dom, block);
  editor_views.set(view.dom, view);
}

function sql_format_options_from_element(element) {
  const language = element.getAttribute('data-sql-format-language') || DEFAULT_SQL_FORMAT_OPTIONS.language;
  const keyword_case = element.getAttribute('data-sql-format-keyword-case') || DEFAULT_SQL_FORMAT_OPTIONS.keywordCase;
  const tab_width = parseInt(element.getAttribute('data-sql-format-tab-width') || String(DEFAULT_SQL_FORMAT_OPTIONS.tabWidth), 10);
  const use_tabs = data_flag(element, 'data-sql-format-use-tabs', DEFAULT_SQL_FORMAT_OPTIONS.useTabs);
  return {
    language,
    keywordCase: keyword_case,
    tabWidth: Number.isFinite(tab_width) ? tab_width : DEFAULT_SQL_FORMAT_OPTIONS.tabWidth,
    useTabs: use_tabs
  };
}

function format_status_element(textarea) {
  const id = textarea.id;
  if (!id) return null;
  return document.querySelector(`[data-code-editor-format-status="${id}"]`);
}

function set_format_status(textarea, message) {
  const status = format_status_element(textarea);
  if (!status) return;
  status.textContent = message || '';
}

export function format_sql_textarea(textarea) {
  if (!textarea) return false;
  const view = editor_views.get(textarea);
  const current = view ? view.state.doc.toString() : textarea.value;
  if (!current || !current.trim()) return true;

  try {
    const formatted = format_sql(current, sql_format_options_from_element(textarea));
    if (view) {
      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: formatted }
      });
    } else {
      textarea.value = formatted;
    }
    set_format_status(textarea, '');
    return true;
  } catch (error) {
    set_format_status(textarea, 'SQL konnte nicht formatiert werden.');
    console.warn('code_editor: SQL format failed', error);
    return false;
  }
}

function bind_format_buttons() {
  if (format_listener_bound) return;
  format_listener_bound = true;

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-code-editor-format]');
    if (!trigger) return;
    event.preventDefault();
    const textarea = document.getElementById(trigger.getAttribute('data-code-editor-format'));
    format_sql_textarea(textarea);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const trigger = event.target.closest('[data-code-editor-format]');
    if (!trigger) return;
    event.preventDefault();
    const textarea = document.getElementById(trigger.getAttribute('data-code-editor-format'));
    format_sql_textarea(textarea);
  });
}

export function refresh_code_editors(root = document) {
  root.querySelectorAll('.cm-editor').forEach((element) => {
    const view = editor_views.get(element) || EditorView.findFromDOM(element);
    if (view) view.requestMeasure();
  });
}

function bind_tab_refresh() {
  if (tab_refresh_bound) return;
  if (typeof window.jQuery !== 'function') return;
  tab_refresh_bound = true;
  const $ = window.jQuery;
  $(document).on('tabs-activate tabs-activate-state tabs-load', '#tabs', function () {
    window.requestAnimationFrame(() => refresh_code_editors(this));
  });
  window.addEventListener('resize', () => refresh_code_editors());
}

export function init_code_editors(root = document) {
  root.querySelectorAll('textarea[data-code-editor]').forEach(mount_from_textarea);
  root.querySelectorAll('.code-block[data-language], pre[data-code-editor]').forEach(mount_readonly_block);
  bind_format_buttons();
  bind_tab_refresh();
}
